
import { useEffect } from 'react';
import { Member, MemberPermission } from '../../types/memberTypes';
import { getAuth } from 'firebase/auth';
import { useFirebase } from '@/context/FirebaseProvider'; // Changed from useData
import { 
  isUserPrimaryOwner, 
  isUserOwner,
  canManageBusinessUsers,
  getEffectivePermission
} from '../../services/permissionService';

/**
 * Hook for handling the current member's state and permissions
 */
export const useCurrentMemberState = (
  members: Member[],
  setMembers: React.Dispatch<React.SetStateAction<Member[]>>,
  source: 'portfolio' | 'business' = 'portfolio'
) => {
  const { userData } = useFirebase(); // Changed from useData to useFirebase
  const auth = getAuth();
  
  // Get the current authenticated user's ID
  const currentAuthUserId = auth.currentUser?.uid;
  
  // Ensure each member's isCurrentUser flag is set correctly based on auth ID
  useEffect(() => {
    if (currentAuthUserId && members.length > 0) {
      const updatedMembers = members.map(member => ({
        ...member,
        isCurrentUser: member.userId === currentAuthUserId || member.id === currentAuthUserId
      }));
      
      // Only update if there's a change
      const hasChanges = updatedMembers.some((m, i) => m.isCurrentUser !== members[i].isCurrentUser);
      if (hasChanges) {
        console.log(`useCurrentMemberState - Updated isCurrentUser flags based on auth ID: ${currentAuthUserId}`);
        setMembers(updatedMembers);
      }
    }
  }, [members, currentAuthUserId, setMembers]);
  
  // Get the current member in the current context (business or portfolio)
  const currentMember = members.find(member => member.isCurrentUser === true);
  
  // Log for debugging
  console.log(`useCurrentMemberState - Found current member:`, currentMember);
  console.log(`useCurrentMemberState - Current auth user ID:`, currentAuthUserId);
  
  // Verify currentUser data from userData is correctly mapped to members
  useEffect(() => {
    if (currentAuthUserId && userData && members.length > 0) { // Changed appData.currentUser to userData
      // Check if current user exists in members with correct data
      const currentUserInMembers = members.find(m => m.isCurrentUser === true);
      
      if (!currentUserInMembers) {
        console.log("Current user not found in members, adding from userData"); // Changed appData to userData
        
        // Get role from currentUser in userData
        const currentUserData = userData; // Changed appData.currentUser to userData
        const role = currentUserData.role || 'Manager';
        
        // Ensure permissions are correctly typed with required fields
        const safePermissions: MemberPermission[] = (currentUserData.permissions || []).map(p => ({
          id: p.id || 'unknown',
          name: p.name || 'Unknown Permission',
          checked: p.checked || false
        }));
        
        // Create a new member based on currentUser data
        const newMember: Member = {
          id: currentAuthUserId,
          userId: currentAuthUserId,
          firstName: currentUserData.firstName || '',
          lastName: currentUserData.lastName || '',
          email: currentUserData.email || '',
          phone: currentUserData.phone || '',
          role: role as any, // Cast to appropriate role type
          permissions: safePermissions,
          status: 'active',
          isCurrentUser: true,
          avatarUrl: currentUserData.avatarUrl,
          avatarInitials: currentUserData.avatarInitials || 
            (currentUserData.firstName?.charAt(0) || '') + 
            (currentUserData.lastName?.charAt(0) || '')
        };
        
        setMembers([...members, newMember]);
      }
    }
  }, [members, currentAuthUserId, setMembers, userData]); // Changed appData.currentUser to userData
  
  // Check if the current member is a primary owner
  const isCurrentMemberPrimaryOwner = currentMember?.role === 'Primary Owner';
  
  // Check if the current member is an owner
  const isCurrentMemberOwner = currentMember?.role === 'Owner' || isCurrentMemberPrimaryOwner;
  
  // Determine if business members can be managed by the current member
  const canBusinessMembersBeManaged = canManageBusinessUsers(
    members,
    source === 'business' ? members : null,
    source
  );
  
  // Get effective permission for actions based on role and context
  const effectivePermissionForActions = getEffectivePermission(
    members,
    source === 'business' ? members : null,
    source
  );
  
  // Check if the current member has the portfolioUsers permission
  const hasManageMembersPermission = currentMember?.permissions?.some(p => 
    p.id === 'portfolioUsers' && p.checked
  ) || false;
  
  // Check if the current member has the businessUsers permission
  const hasBusinessMembersPermission = currentMember?.permissions?.some(p => 
    p.id === 'businessUsers' && p.checked
  ) || false;
  
  return {
    currentMember,
    isCurrentMemberPrimaryOwner,
    isCurrentMemberOwner,
    canBusinessMembersBeManaged,
    effectivePermissionForActions,
    hasManageMembersPermission,
    hasBusinessMembersPermission
  };
};
